import { Form, Input, Modal, Select } from "antd";
import React, { useCallback, useMemo, useState } from "react";
import { graphql, useMutation, usePreloadedQuery } from "react-relay";

import * as CLOUD_API from "@skydio/api_util/src/backends/cloud_api";
import { OrgTier } from "@skydio/pbtypes/pbtypes/tools/cloud_api/enums_pb";
import { Checkbox, Tag } from "@skydio/rivet-ui";

import OrganizationsFilter from "components/common/filters/OrganizationsFilter";
import SalesforceAccountSelector from "components/common/filters/SalesforceAccountSelector";
import { useIsCjisOrgReady } from "hooks/useOrgDataLevel";
import { displayMutationResults } from "utils/mutations";

import type { FC } from "react";
import type { PreloadedQuery } from "react-relay";
import type {
  CreateOrganizationModalMutation,
  datalevel,
  orgtier,
} from "__generated__/CreateOrganizationModalMutation.graphql";
import type { CreateOrganizationModalQuery as QueryType } from "__generated__/CreateOrganizationModalQuery.graphql";

const { dataLevelEnum } = CLOUD_API.modules.organizations.utils;

const SFDC_SEGMENT_GROUP_LAW_ENFORCEMENT = "Law Enforcement";

export const CreateOrganizationModalQuery = graphql`
  query CreateOrganizationModalQuery {
    salesforce {
      enabled
    }
  }
`;

interface Props {
  visible: boolean;
  close: () => void;
  onOrganizationCreated: () => void;
  queryRef: PreloadedQuery<QueryType>;
}

interface CreateOrganizationFormFields {
  name: string;
  salesforceAccountId?: string;
  dataLevel: datalevel;
  orgTier: orgtier;
  templateOrgUuid: string;
}

const CreateOrganizationModal: FC<Props> = ({
  visible,
  close,
  onOrganizationCreated,
  queryRef,
}) => {
  const [form] = Form.useForm<CreateOrganizationFormFields>();
  const [isCustomerOrg, setIsCustomerOrg] = useState(true);

  const showCjisDatalevel = useIsCjisOrgReady();

  const data = usePreloadedQuery(CreateOrganizationModalQuery, queryRef);

  const dismissModal = useCallback(() => {
    setIsCustomerOrg(true); // Reset to default
    form.resetFields();
    close();
  }, [form, close]);

  const [createOrganization] = useMutation<CreateOrganizationModalMutation>(graphql`
    mutation CreateOrganizationModalMutation(
      $name: String!
      $dataLevel: datalevel
      $orgTier: orgtier
      $templateOrgUuid: String
      $salesforceAccountId: String
      $validateFieldsAgainstSalesforce: Boolean!
    ) {
      createOrganization(
        name: $name
        dataLevel: $dataLevel
        orgTier: $orgTier
        templateOrgUuid: $templateOrgUuid
        salesforceAccountId: $salesforceAccountId
        validateFieldsAgainstSalesforce: $validateFieldsAgainstSalesforce
      ) {
        organization {
          id
          uuid
          name
          orgTier
        }
        errors
      }
    }
  `);

  const handleOk = useCallback(async () => {
    const { name, salesforceAccountId, dataLevel, orgTier, templateOrgUuid } =
      await form.validateFields();
    if (name) {
      createOrganization({
        variables: {
          name,
          salesforceAccountId,
          dataLevel,
          orgTier,
          templateOrgUuid,
          validateFieldsAgainstSalesforce: data.salesforce.enabled,
        },
        onCompleted: displayMutationResults(response => response.createOrganization, {
          successMessage: o => (
            <>
              <a href={`/o/${o.organization?.uuid}/`}>{o.organization?.name}</a>&nbsp;created
            </>
          ),
          onSuccess: () => {
            onOrganizationCreated();
            dismissModal();
          },
        }),
      });
    }
  }, [form, createOrganization, data.salesforce.enabled, onOrganizationCreated, dismissModal]);

  let validDataLevels = Object.keys(dataLevelEnum);
  let defaultDataLevel = "EXTERNAL";
  let validOrgTiers = Object.keys(OrgTier.OrgTierEnum);
  let defaultOrgTier = "UNKNOWN";

  if (data.salesforce.enabled) {
    validDataLevels = validDataLevels.filter(tier => tier !== "INTERNAL");
    if (!isCustomerOrg) {
      validDataLevels = ["INTERNAL"];
      defaultDataLevel = "INTERNAL";
    }

    validOrgTiers = validOrgTiers.filter(tier => tier !== "EMPLOYEE");
    if (!isCustomerOrg) {
      validOrgTiers = ["EMPLOYEE"];
      defaultOrgTier = "EMPLOYEE";
    }
  }

  const handleCustomerOrgCheckboxChange = useCallback(
    (value: boolean) => {
      setIsCustomerOrg(value);
      form.resetFields(["salesforceAccountId"]);
      if (!value) {
        form.setFieldValue("dataLevel", "INTERNAL");
        form.setFieldValue("orgTier", "EMPLOYEE");
      } else {
        form.setFieldValue("dataLevel", "EXTERNAL");
        form.setFieldValue("orgTier", "T3");
      }
    },
    [form, setIsCustomerOrg]
  );

  const handleSelectedSalesforceAccountChange = useCallback(
    (account: { accountId: string; orgTier: orgtier | null; segmentGroup: string | null }) => {
      form.setFieldValue("salesforceAccountId", account.accountId);
      form.setFieldValue("orgTier", account.orgTier);
      if (account.segmentGroup == SFDC_SEGMENT_GROUP_LAW_ENFORCEMENT) {
        form.setFieldValue("dataLevel", "SECRET");
      } else {
        form.setFieldValue("dataLevel", "EXTERNAL");
      }
    },
    [form]
  );

  const handleCopyGroupsFromOrgChange = useCallback(
    (value: string[]) => {
      form.setFieldValue("templateOrgUuid", value?.[0] ?? undefined);
    },
    [form]
  );

  const formInitalValues = useMemo(() => {
    return {
      name: "",
      dataLevel: defaultDataLevel,
      orgTier: defaultOrgTier,
    };
  }, [defaultDataLevel, defaultOrgTier]);

  const requiredFormRule = useMemo(() => {
    return [
      {
        required: true,
      },
    ];
  }, []);

  const defaultOrgGroupsValue = useMemo(() => {
    return [];
  }, []);

  const copyGroupsLabel = useMemo(() => {
    return (
      <span>
        <Tag color="primary" variant="bordered">
          Optional
        </Tag>
        Copy Groups from Organization
      </span>
    );
  }, []);

  return (
    <Modal
      title="Create a new organization"
      okText="Create"
      cancelText="Cancel"
      visible={visible}
      onCancel={dismissModal}
      onOk={handleOk}
      transitionName=""
    >
      <Form form={form} layout="vertical" initialValues={formInitalValues} requiredMark={true}>
        <Form.Item name="name" label="Name" rules={requiredFormRule}>
          <Input />
        </Form.Item>
        {data.salesforce.enabled && (
          <>
            <div className="mb-4">
              <Checkbox isSelected={isCustomerOrg} onValueChange={handleCustomerOrgCheckboxChange}>
                <span>This is a customer organization</span>
              </Checkbox>
            </div>
            {isCustomerOrg && (
              <Form.Item name="salesforceAccountId" label="Salesforce Account" required>
                <SalesforceAccountSelector
                  onSelectedAccountChange={handleSelectedSalesforceAccountChange}
                />
              </Form.Item>
            )}
          </>
        )}
        <Form.Item name="dataLevel" label="Data Level" required>
          <Select defaultValue={defaultDataLevel} disabled={validDataLevels.length === 1}>
            {validDataLevels.map(key => {
              if (key !== "UNKNOWN" && (showCjisDatalevel || key !== "CJIS")) {
                return <Select value={key}>{key}</Select>;
              }
              return null;
            })}
          </Select>
        </Form.Item>
        <Form.Item name="orgTier" label="Organization Tier" required>
          <Select defaultValue={defaultOrgTier} disabled={validOrgTiers.length === 1}>
            {validOrgTiers.map(key => {
              if (key !== "UNKNOWN") {
                return <Select value={key}>{key}</Select>;
              }
              return null;
            })}
          </Select>
        </Form.Item>
        <Form.Item name="templateOrgUuid" label={copyGroupsLabel}>
          <OrganizationsFilter
            placeholder={"Select an organization..."}
            value={defaultOrgGroupsValue}
            onChange={handleCopyGroupsFromOrgChange}
            maxSelectionCount={1}
          ></OrganizationsFilter>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateOrganizationModal;
